import { EXAM_TASKS, type ExamTaskKey } from '@fluent/types';
import type { Json } from '@fluent/types';
import { betterResult, type ExamAnswers, type ExamResult, type TaskAnswers } from './attempt.js';
import { numberOf, testIdOf } from './catalog.js';

/**
 * Finished sittings on the server (`exam_results`, migration 0022) — pure
 * conversions and the merge with this device's copy, so they are unit-tested
 * without Supabase or storage.
 *
 * The device keeps only "best" and "last" per test (examStore). The server
 * keeps every finished sitting; after a fetch the two are merged so a new
 * phone, a cleared browser or a second device shows the same list.
 */

export type ResultsByTest = Record<string, { best: ExamResult; last: ExamResult }>;

/** Columns read from exam_results — everything the app shows. */
export const EXAM_RESULT_COLUMNS =
  'attempt_id, test_number, score, by_task, answers, elapsed_ms, timed_out, finished_at' as const;

export interface ExamResultRow {
  attempt_id: string;
  test_number: number;
  score: number;
  by_task: Json;
  answers: Json;
  elapsed_ms: number;
  timed_out: boolean;
  finished_at: string;
}

/** What save_exam_result takes. */
export interface ExamResultPayload {
  p_attempt_id: string;
  p_test: number;
  p_by_task: Json;
  p_answers: Json;
  p_elapsed_ms: number;
  p_timed_out: boolean;
  p_finished_at: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Only letters survive: a malformed blob becomes empty answers, never a crash. */
function answersOf(v: unknown): ExamAnswers {
  const out = {} as ExamAnswers;
  const src = isRecord(v) ? v : {};
  for (const { key } of EXAM_TASKS) {
    const task: TaskAnswers = {};
    const raw = src[key];
    if (isRecord(raw)) {
      for (const [i, letter] of Object.entries(raw)) {
        const index = Number(i);
        if (Number.isInteger(index) && index >= 0 && typeof letter === 'string') task[index] = letter;
      }
    }
    out[key] = task;
  }
  return out;
}

function byTaskOf(v: unknown): Record<ExamTaskKey, number> {
  const src = isRecord(v) ? v : {};
  const out = {} as Record<ExamTaskKey, number>;
  for (const { key } of EXAM_TASKS) {
    const n = src[key];
    out[key] = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  }
  return out;
}

export function rowToResult(row: ExamResultRow): ExamResult {
  return {
    attemptId: row.attempt_id,
    testId: testIdOf(row.test_number),
    score: row.score,
    byTask: byTaskOf(row.by_task),
    answers: answersOf(row.answers),
    elapsedMs: row.elapsed_ms,
    finishedAt: Date.parse(row.finished_at),
    timedOut: row.timed_out,
  };
}

/** null for a result the server can't take (no attempt id, unknown test). */
export function resultToPayload(result: ExamResult): ExamResultPayload | null {
  const test = numberOf(result.testId);
  if (!result.attemptId || test === null) return null;
  return {
    p_attempt_id: result.attemptId,
    p_test: test,
    p_by_task: result.byTask as unknown as Json,
    p_answers: result.answers as unknown as Json,
    p_elapsed_ms: Math.max(0, Math.round(result.elapsedMs)),
    p_timed_out: result.timedOut,
    p_finished_at: new Date(result.finishedAt).toISOString(),
  };
}

const identity = (r: ExamResult) => r.attemptId ?? `${r.testId}@${r.finishedAt}`;

/**
 * Merge the server's sittings into this device's best/last. Per test:
 * last = the newest finished; best = the highest score, a tie going to the
 * newer (betterResult's rule). Returns `local` itself when nothing changed,
 * so a store update can be skipped.
 */
export function mergeResults(local: ResultsByTest, remote: readonly ExamResult[]): ResultsByTest {
  const byTest = new Map<string, Map<string, ExamResult>>();
  const add = (r: ExamResult) => {
    const m = byTest.get(r.testId) ?? new Map<string, ExamResult>();
    if (!m.has(identity(r))) m.set(identity(r), r);
    byTest.set(r.testId, m);
  };
  for (const { best, last } of Object.values(local)) {
    add(best);
    add(last);
  }
  for (const r of remote) add(r);

  let changed = false;
  const out: ResultsByTest = {};
  for (const [testId, m] of byTest) {
    const oldestFirst = [...m.values()].sort((a, b) => a.finishedAt - b.finishedAt);
    const last = oldestFirst[oldestFirst.length - 1]!;
    const best = oldestFirst.reduce<ExamResult | undefined>((acc, r) => betterResult(acc, r), undefined)!;
    const prev = local[testId];
    if (prev && identity(prev.best) === identity(best) && identity(prev.last) === identity(last)) {
      out[testId] = prev;
    } else {
      changed = true;
      out[testId] = { best, last };
    }
  }
  return changed ? out : local;
}
